<!-- Show simple textarea for Cypress tests -->
<div *ngIf="isRunningInCypress && showEditor" class="cypress-textarea-container">
  <textarea
    [attr.id]="field.id"
    [formControl]="formControl"
    [placeholder]="field?.props?.placeholder"
    [style.minHeight.px]="field?.props?.height || 300"
    class="form-control"
  ></textarea>
  <div class="form-text text-muted">
    <span [innerHTML]="'Use the <strong>@</strong> sign and start typing to mention any user.' | translate"></span
    >&nbsp;
    <span
      [innerHTML]="'Use the <strong>#</strong> sign and start typing to insert an image from your gallery.' | translate"
    ></span>
  </div>
</div>

<!-- Custom toolbar for the editor (not shown in Cypress) -->
<div
  *ngIf="!isRunningInCypress"
  [class.cke-toolbar-maximized]="isMaximized"
  [class.disabled]="isReadOnly"
  [hidden]="!showEditor"
  class="custom-ckeditor-toolbar"
>
  <!-- Minimize button that appears when editor is maximized -->
  <div *ngIf="isMaximized" class="toolbar-group minimize-group">
    <button
      (click)="execCommand('maximize')"
      class="toolbar-btn action-btn"
      title="{{ 'Exit full screen' | translate }}"
      type="button"
    >
      <fa-icon [icon]="['fas', 'compress']"></fa-icon>
    </button>
  </div>

  <!-- Main toolbar - highest frequency, low-disruption actions -->
  <div class="toolbar-group">
    <button (click)="execCommand('bold')" class="toolbar-btn action-btn" title="{{ 'Bold' | translate }}" type="button">
      <fa-icon [icon]="['fas', 'bold']"></fa-icon>
    </button>
    <button
      (click)="execCommand('italic')"
      class="toolbar-btn action-btn"
      title="{{ 'Italic' | translate }}"
      type="button"
    >
      <fa-icon [icon]="['fas', 'italic']"></fa-icon>
    </button>
  </div>

  <div class="toolbar-group">
    <button
      (click)="execCommand('bulletedlist')"
      class="toolbar-btn action-btn"
      title="{{ 'Bulleted list' | translate }}"
      type="button"
    >
      <fa-icon [icon]="['fas', 'list-ul']"></fa-icon>
    </button>
    <button
      (click)="execCommand('blockquote')"
      class="toolbar-btn action-btn"
      title="{{ 'Quote' | translate }}"
      type="button"
    >
      <fa-icon [icon]="['fas', 'quote-left']"></fa-icon>
    </button>
  </div>

  <div class="toolbar-group">
    <button
      (click)="execCommand('link')"
      class="toolbar-btn action-btn"
      title="{{ 'Insert link' | translate }}"
      type="button"
    >
      <fa-icon [icon]="['fas', 'link']"></fa-icon>
    </button>
    <button
      (click)="execCommand('addImage')"
      class="toolbar-btn action-btn"
      title="{{ 'Insert image' | translate }}"
      type="button"
    >
      <fa-icon [icon]="['fas', 'image']"></fa-icon>
    </button>
    <button
      (click)="execCommand('smiley')"
      class="toolbar-btn action-btn"
      title="{{ 'Emoji' | translate }}"
      type="button"
    >
      <fa-icon [icon]="['fas', 'smile']"></fa-icon>
    </button>
  </div>

  <!-- More options dropdown for less frequently used features -->
  <div class="toolbar-group py-0" ngbDropdown placement="bottom-right">
    <button
      id="editorMoreOptions"
      class="toolbar-btn options-btn no-toggle"
      ngbDropdownToggle
      title="{{ 'More options' | translate }}"
      type="button"
    >
      <fa-icon [icon]="['fas', 'ellipsis-v']"></fa-icon>
    </button>
    <div aria-labelledby="editorMoreOptions" ngbDropdownMenu>
      <!-- Text styling -->
      <button (click)="execCommand('underline')" ngbDropdownItem type="button">
        <fa-icon [icon]="['fas', 'underline']" class="me-2"></fa-icon>{{ "Underline" | translate }}
      </button>
      <button (click)="execCommand('strike')" ngbDropdownItem type="button">
        <fa-icon [icon]="['fas', 'strikethrough']" class="me-2"></fa-icon>{{ "Strike through" | translate }}
      </button>
      <button (click)="execCommand('numberedlist')" ngbDropdownItem type="button">
        <fa-icon [icon]="['fas', 'list-ol']" class="me-2"></fa-icon>{{ "Numbered list" | translate }}
      </button>
      <button (click)="execCommand('unlink')" ngbDropdownItem type="button">
        <fa-icon [icon]="['fas', 'unlink']" class="me-2"></fa-icon>{{ "Remove link" | translate }}
      </button>
      <div class="dropdown-divider"></div>

      <!-- Font size -->
      <button (click)="execCommand('fontsize:50%')" ngbDropdownItem type="button">
        <fa-icon [icon]="['fas', 'text-height']" class="me-2"></fa-icon>{{ "XS" | translate }}
      </button>
      <button (click)="execCommand('fontsize:75%')" ngbDropdownItem type="button">
        <fa-icon [icon]="['fas', 'text-height']" class="me-2"></fa-icon>{{ "S" | translate }}
      </button>
      <button (click)="execCommand('fontsize:100%')" ngbDropdownItem type="button">
        <fa-icon [icon]="['fas', 'text-height']" class="me-2"></fa-icon>{{ "M" | translate }}
      </button>
      <button (click)="execCommand('fontsize:150%')" ngbDropdownItem type="button">
        <fa-icon [icon]="['fas', 'text-height']" class="me-2"></fa-icon>{{ "L" | translate }}
      </button>
      <button (click)="execCommand('fontsize:200%')" ngbDropdownItem type="button">
        <fa-icon [icon]="['fas', 'text-height']" class="me-2"></fa-icon>{{ "XL" | translate }}
      </button>
      <div class="dropdown-divider"></div>

      <!-- Special content -->
      <button (click)="execCommand('specialchar')" ngbDropdownItem type="button">
        <fa-icon [icon]="['fas', 'font']" class="me-2"></fa-icon>{{ "Special character" | translate }}
      </button>
      <button (click)="execCommand('codeSnippet')" ngbDropdownItem type="button">
        <fa-icon [icon]="['fas', 'laptop-code']" class="me-2"></fa-icon>{{ "Code snippet" | translate }}
      </button>
      <button (click)="execCommand('addFile')" ngbDropdownItem type="button">
        <fa-icon [icon]="['fas', 'file']" class="me-2"></fa-icon>{{ "Upload file" | translate }}
      </button>
      <div class="dropdown-divider"></div>

      <!-- Editor operations -->
      <button (click)="execCommand('sourcedialog')" ngbDropdownItem type="button">
        <fa-icon [icon]="['fas', 'code']" class="me-2"></fa-icon>{{ "Source" | translate }}
      </button>
      <button
        [class.disabled]="isMaximized"
        [class.text-muted]="isMaximized"
        (click)="!isMaximized && execCommand('maximize')"
        ngbDropdownItem
        type="button"
      >
        <fa-icon [icon]="['fas', 'expand']" class="me-2"></fa-icon>{{ "Maximize" | translate }}
      </button>
      <button (click)="execCommand('undo')" ngbDropdownItem type="button">
        <fa-icon [icon]="['fas', 'undo']" class="me-2"></fa-icon>{{ "Undo" | translate }}
      </button>
      <button (click)="execCommand('redo')" ngbDropdownItem type="button">
        <fa-icon [icon]="['fas', 'redo']" class="me-2"></fa-icon>{{ "Redo" | translate }}
      </button>
    </div>
  </div>
</div>

<!-- Editor content that's shown when the editor is ready (not shown in Cypress) -->
<div *ngIf="!isRunningInCypress" [hidden]="!showEditor">
  <textarea [attr.id]="field.id" [formControl]="formControl" class="form-control"></textarea>

  <div class="form-text text-muted">
    <span [innerHTML]="'Use the <strong>@</strong> sign and start typing to mention any user.' | translate"></span
    >&nbsp;
    <span
      [innerHTML]="'Use the <strong>#</strong> sign and start typing to insert an image from your gallery.' | translate"
    ></span>
  </div>
</div>

<!-- Loading indicator shown while editor is loading -->
<div *ngIf="isLoading" [class.disabled]="isReadOnly" class="editor-loading-container mt-4">
  <astrobin-loading-indicator></astrobin-loading-indicator>
  <div class="text-center mt-2 text-muted small">
    <span>{{ "Loading editor..." | translate }}</span>
  </div>
</div>

<!-- Error message shown if editor fails to load -->
<div *ngIf="hasError" class="editor-error-container mt-4 alert alert-dark">
  <div class="text-center">
    <p>{{ "Failed to load the editor." | translate }}</p>
    <button (click)="_initializeEditor()" class="btn btn-sm btn-outline-primary" type="button">
      {{ "Try again" | translate }}
    </button>
  </div>
</div>
